/**
 * Chart Helper Functions for Analytics
 * Reusable data transformation utilities
 */

/**
 * Count occurrences of values in an array of objects by a specific key
 */
export const countBy = (array, key) => {
  const counts = {};
  array.forEach(item => {
    const value = item[key];
    if (value !== null && value !== undefined && value !== '') {
      counts[value] = (counts[value] || 0) + 1;
    }
  });
  return counts;
};

/**
 * Get top N items from counts object, sorted by value
 */
export const getTopN = (counts, n = 10) => {
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([name, value]) => ({ name, value }));
};

/**
 * Get most common value for a key
 */
export const getMostCommon = (array, key) => {
  const counts = countBy(array, key);
  const entries = Object.entries(counts);
  if (entries.length === 0) return null;

  const [mostCommon, count] = entries.sort((a, b) => b[1] - a[1])[0];
  return { value: mostCommon, count };
};

/**
 * Group array by month from a date field
 */
export const groupByMonth = (array, dateKey) => {
  const monthCounts = {};

  array.forEach(item => {
    const dateStr = item[dateKey];
    if (!dateStr) return;

    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return;

      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthCounts[monthKey] = (monthCounts[monthKey] || 0) + 1;
    } catch (e) {
      // Skip invalid dates
    }
  });

  // Convert to array and sort by date
  return Object.entries(monthCounts)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([month, count]) => ({
      name: formatMonthKey(month),
      value: count
    }));
};

/**
 * Group array by year from a date field
 */
export const groupByYear = (array, dateKey) => {
  const yearCounts = {};

  array.forEach(item => {
    const dateStr = item[dateKey];
    if (!dateStr) return;

    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return;

      const year = date.getFullYear();
      yearCounts[year] = (yearCounts[year] || 0) + 1;
    } catch (e) {
      // Skip invalid dates
    }
  });

  return Object.entries(yearCounts)
    .sort((a, b) => a[0] - b[0])
    .map(([year, count]) => ({
      name: year,
      value: count
    }));
};

/**
 * Get date range from array
 */
export const getDateRange = (array, dateKey) => {
  const dates = array
    .map(item => {
      try {
        const date = new Date(item[dateKey]);
        return isNaN(date.getTime()) ? null : date;
      } catch {
        return null;
      }
    })
    .filter(d => d !== null);

  if (dates.length === 0) return null;

  const min = new Date(Math.min(...dates));
  const max = new Date(Math.max(...dates));

  return {
    min: min.toISOString().split('T')[0],
    max: max.toISOString().split('T')[0],
    span: `${min.getFullYear()} - ${max.getFullYear()}`
  };
};

/**
 * Format month key (YYYY-MM) to readable format
 */
const formatMonthKey = (monthKey) => {
  const [year, month] = monthKey.split('-');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${monthNames[parseInt(month) - 1]} ${year}`;
};

/**
 * Calculate percentage
 */
export const calculatePercentage = (part, total) => {
  if (total === 0) return 0;
  return ((part / total) * 100).toFixed(1);
};

/**
 * Group numeric values into histogram bins
 */
export const createHistogram = (array, key, bins = 5) => {
  const values = array
    .map(item => item[key])
    .filter(v => v !== null && v !== undefined && !isNaN(v));

  if (values.length === 0) return [];

  const min = Math.min(...values);
  const max = Math.max(...values);
  const binSize = (max - min) / bins;

  const histogram = Array(bins).fill(0).map((_, i) => ({
    name: `${formatNumber(min + i * binSize)} - ${formatNumber(min + (i + 1) * binSize)}`,
    value: 0
  }));

  values.forEach(value => {
    let binIndex = Math.floor((value - min) / binSize);
    if (binIndex >= bins) binIndex = bins - 1; // Edge case for max value
    histogram[binIndex].value++;
  });

  return histogram;
};

/**
 * Format large numbers
 */
const formatNumber = (num) => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toFixed(0);
};

/**
 * Calculate summary statistics
 */
export const calculateStats = (array, key) => {
  const values = array
    .map(item => item[key])
    .filter(v => v !== null && v !== undefined && !isNaN(v));

  if (values.length === 0) return null;

  const sum = values.reduce((a, b) => a + b, 0);
  const mean = sum / values.length;
  const sorted = [...values].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];

  return {
    count: values.length,
    sum,
    mean,
    median,
    min: Math.min(...values),
    max: Math.max(...values)
  };
};

/**
 * Truncate string with ellipsis
 */
export const truncate = (str, maxLength = 30) => {
  if (!str) return '';
  return str.length > maxLength ? `${str.substring(0, maxLength)}...` : str;
};
